import { useDealerContext } from "@porsche-customer/slfinrtl-context"
import { useToastManager } from "@porsche-design-system/components-react"
import type { ExteriorColorOptionView } from "@slfinrtl/ui-bff-routes"
import { getExteriorColorOptionsRoute, setSelectedExteriorColorRoute } from "@slfinrtl/ui-bff-routes"
import { useEffect, useMemo, useRef, useState } from "react"
import { useIntl } from "react-intl"
import { formMessages } from "src/common/form.messages"
import { useBffDynamic } from "src/hooks"
import type { SelectCatalogExteriorColorProps } from "../../components/SelectCatalogExteriorColor"
import type { SelectCatalogExteriorColorContainerProps } from "./SelectCatalogExteriorColorContainer"

export const useSelectCatalogExteriorColor: (
	props: SelectCatalogExteriorColorContainerProps,
) => SelectCatalogExteriorColorProps | undefined = ({
	currentExteriorColor,
	inventoryId,
	onExteriorColorSelected,
	orderType,
	showCustomExteriorColorContainer,
}) => {
	const { formatMessage } = useIntl()
	const { locale } = useDealerContext()

	const { addMessage } = useToastManager()
	const addMessageRef = useRef(addMessage)

	const [selectedOptionCode, setSelectedOptionCode] = useState<string | undefined>(
		currentExteriorColor?.optionCode ?? undefined,
	)
	const [allOptions, setAllOptions] = useState<ExteriorColorOptionView[]>([])
	const [selectedColorTagKey, setSelectedColorTagKey] = useState<string | null>(null)
	const [selectedColorTypeKey, setSelectedColorTypeKey] = useState<string | null>(null)
	const [searchText, setSearchText] = useState<string>("")
	const [isLoading, setIsLoading] = useState<boolean>(true)
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

	const getExteriorColorOptions = useBffDynamic(getExteriorColorOptionsRoute, "inline")
	useEffect(() => {
		if (orderType.code) {
			getExteriorColorOptions({
				body: undefined,
				pathParams: {},
				queryParams: {
					"model-year": orderType.modelYear.toString(),
					"order-type-code": orderType.code,
				},
			})
				.then(setAllOptions)
				.finally(() => setIsLoading(false))
		} else {
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setAllOptions([])
			setIsLoading(false)
		}
	}, [getExteriorColorOptions, orderType])

	const filteredOptions = useMemo(
		() => calculateFilteredOptions(allOptions, selectedColorTagKey, selectedColorTypeKey, searchText),
		[allOptions, selectedColorTagKey, selectedColorTypeKey, searchText],
	)

	const filteredTags = useMemo(() => calculateColorTagOptions(allOptions), [allOptions])

	const filteredTypes = useMemo(() => calculateColorTypeOptions(allOptions), [allOptions])

	const submitSelectedExteriorColor = useBffDynamic(setSelectedExteriorColorRoute, "inline")
	const onSubmit = async () => {
		if (selectedOptionCode) {
			setIsSubmitting(true)
			try {
				await submitSelectedExteriorColor({
					body: {
						optionCode: selectedOptionCode,
					},
					pathParams: {},
					queryParams: { "inventory-id": inventoryId },
				})
			} catch {
				addMessageRef.current({
					state: "info",
					text: formatMessage(formMessages.errorGeneral),
				})
				return
			} finally {
				setIsSubmitting(false)
			}

			const selectedOption = allOptions.find((option) => option.optionCode === selectedOptionCode)
			if (selectedOption) {
				onExteriorColorSelected({
					hexCode: selectedOption.hexCode,
					name: {
						[locale]: selectedOption.name,
					},
					optionCode: selectedOption.optionCode,
					tagKey: undefined,
					typeKey: undefined,
				})
			}
		}
	}

	return !isLoading
		? {
				exteriorColor: {
					options: filteredOptions,
					setValue: setSelectedOptionCode,
					value: selectedOptionCode,
				},
				isSubmitting,
				onSubmit,
				searchText: {
					setValue: setSearchText,
					value: searchText,
				},
				showCustomExteriorColorContainer,
				tags: {
					options: filteredTags,
					setValue: setSelectedColorTagKey,
					value: selectedColorTagKey,
				},
				types: {
					options: filteredTypes,
					setValue: setSelectedColorTypeKey,
					value: selectedColorTypeKey,
				},
			}
		: undefined
}

const calculateFilteredOptions = (
	allOptions: ExteriorColorOptionView[],
	selectedColorTagKey: string | null,
	selectedColorTypeKey: string | null,
	searchText: string,
): ExteriorColorOptionView[] => {
	let filteredOptions = allOptions
	if (selectedColorTagKey) {
		filteredOptions = filteredOptions.filter(
			(exteriorColorOption) => exteriorColorOption.colorTag.key === selectedColorTagKey,
		)
	}
	if (selectedColorTypeKey) {
		filteredOptions = filteredOptions.filter(
			(exteriorColorOption) => exteriorColorOption.colorType.key === selectedColorTypeKey,
		)
	}
	if (searchText !== undefined && searchText.length > 0) {
		const lowerSearchText = searchText.toLowerCase()
		filteredOptions = filteredOptions.filter(
			(exteriorColorOption) =>
				exteriorColorOption.name?.toLocaleLowerCase()?.includes(lowerSearchText) ||
				exteriorColorOption.optionCode.toLocaleLowerCase()?.includes(lowerSearchText) ||
				exteriorColorOption?.colorType?.name?.toLocaleLowerCase()?.includes(lowerSearchText),
		)
	}
	return filteredOptions
}

const calculateColorTagOptions = (
	exteriorColorOptions: ExteriorColorOptionView[],
): SelectCatalogExteriorColorProps["tags"]["options"] => {
	const uniqueColorTags = exteriorColorOptions?.reduce<Record<string, ExteriorColorOptionView["colorTag"]>>(
		(result, current) => {
			if (!(current.colorTag.key in result)) {
				result[current.colorTag.key] = current.colorTag
			}

			return result
		},
		{},
	)

	return Object.values(uniqueColorTags).map((colorTag) => ({
		label: colorTag.name,
		value: colorTag.key,
	}))
}

const calculateColorTypeOptions = (
	exteriorColorOptions: ExteriorColorOptionView[],
): SelectCatalogExteriorColorProps["types"]["options"] => {
	const uniqueColorTypes = exteriorColorOptions?.reduce<Record<string, ExteriorColorOptionView["colorType"]>>(
		(result, current) => {
			if (!(current.colorType.key in result)) {
				result[current.colorType.key] = current.colorType
			}

			return result
		},
		{},
	)

	return Object.values(uniqueColorTypes).map((colorType) => ({
		label: colorType.name,
		value: colorType.key,
	}))
}
